/**
 * SSR-safe обертка для vue-toastification
 * Импортирует toast только на клиенте через динамический import
 */

import type { ToastInterface } from 'vue-toastification';

let toastInstance: ToastInterface | null = null;
let toastModule: any = null;
let loadingPromise: Promise<any> | null = null;

/**
 * Динамический импорт vue-toastification (только на клиенте)
 * Кеширует промис для избежания множественных загрузок
 */
async function loadToastModule() {
    if (process.env.SERVER) {
        return null;
    }

    // Если уже загружен - возвращаем
    if (toastModule) {
        return toastModule;
    }

    // Если идет загрузка - ждем ее завершения
    if (loadingPromise) {
        return loadingPromise;
    }

    // Начинаем загрузку
    loadingPromise = import('vue-toastification')
        .then(module => {
            toastModule = module;
            return module;
        })
        .catch(error => {
            console.error('Failed to load vue-toastification:', error);
            loadingPromise = null; // Сбрасываем для повторной попытки
            return null;
        });

    return loadingPromise;
}

/**
 * Инициализация toast модуля (вызывается при старте приложения)
 * Можно вызвать в app.ts или в onMounted главного компонента
 */
export async function initToast(): Promise<void> {
    if (process.env.SERVER) {
        return;
    }

    const module = await loadToastModule();
    if (module && !toastInstance) {
        try {
            toastInstance = module.useToast();
        } catch (error) {
            console.error('Failed to initialize vue-toastification:', error);
        }
    }
}

/**
 * Безопасный useToast для SSR (синхронный)
 * Возвращает экземпляр toast на клиенте, null на сервере
 * 
 * ВАЖНО: Требует предварительного вызова initToast()
 */
export function useToastSafe(): ToastInterface | null {
    // На сервере возвращаем null
    if (process.env.SERVER) {
        return null;
    }

    // Возвращаем уже инициализированный экземпляр
    return toastInstance;
}

/**
 * Асинхронная версия useToastSafe
 * Автоматически загружает модуль если еще не загружен
 */
export async function useToastSafeAsync(): Promise<ToastInterface | null> {
    // На сервере возвращаем null
    if (process.env.SERVER) {
        return null;
    }

    // Если уже инициализирован - возвращаем
    if (toastInstance) {
        return toastInstance;
    }

    // Инициализируем
    await initToast();
    return toastInstance;
}

/**
 * Получить POSITION enum для toast (синхронный)
 */
export function getToastPosition() {
    // На сервере возвращаем null
    if (process.env.SERVER || !toastModule) {
        return null;
    }

    return toastModule.POSITION;
}

/**
 * Получить POSITION enum для toast (асинхронный)
 */
export async function getToastPositionAsync() {
    if (process.env.SERVER) {
        return null;
    }

    const module = await loadToastModule();
    return module?.POSITION || null;
}

/**
 * Проверить, доступен ли toast (клиентская среда)
 */
export function isToastAvailable(): boolean {
    return !process.env.SERVER && typeof window !== 'undefined';
}

/**
 * Проверить, инициализирован ли toast
 */
export function isToastInitialized(): boolean {
    return toastInstance !== null;
}
